// src/app/Dashboard.js - Fixed version
import React, { useState } from 'react';
import {
  Users,
  FileText,
  RefreshCw,
  Clock,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  FileSpreadsheet,
  Filter,
} from 'lucide-react';
import { useData } from '../context/DataContext';

const Dashboard = ({ setCurrentPage }) => {
  const {
    user,
    clientData,
    sheetConfig,
    isLoading,
    lastFetched,
    error,
    refreshData,
  } = useData();

  const [refreshing, setRefreshing] = useState(false);
  const [filteredStatus, setFilteredStatus] = useState('all');

  // Remove the unused statusStats state variable
  // const [statusStats, setStatusStats] = useState(null);

  // Remove the useEffect that was causing issues
  // useEffect(() => {
  //   if (clientData && clientData.length > 0) {
  //     setStatusStats(getStatusStats());
  //   }
  // }, [clientData, getStatusStats]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const formatLastUpdated = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  // Generate recent activity from client data
  const getRecentActivity = () => {
    if (!clientData || clientData.length === 0) return [];

    // Sort by most recent first (assuming date field exists)
    const sortedClients = [...clientData]
      .filter((client) => client.status)
      .sort((a, b) => {
        // First prioritize completed items
        if (
          a.status.toLowerCase().includes('complete') &&
          !b.status.toLowerCase().includes('complete')
        )
          return -1;
        if (
          !a.status.toLowerCase().includes('complete') &&
          b.status.toLowerCase().includes('complete')
        )
          return 1;

        // Then sort by priority/position if available
        if (a.priority !== undefined && b.priority !== undefined) {
          return parseInt(a.priority) - parseInt(b.priority);
        }
        return 0;
      })
      .slice(0, 5);

    return sortedClients.map((client) => {
      const type = client.status.toLowerCase().includes('complete')
        ? 'completed'
        : client.status.toLowerCase().includes('progress')
        ? 'progress'
        : 'other';

      return {
        type,
        client: client.name,
        status: client.status,
        message: `${client.name} - ${client.status}`,
      };
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'completed':
        return <div className="w-2 h-2 bg-green-500 rounded-full"></div>;
      case 'progress':
        return <div className="w-2 h-2 bg-blue-500 rounded-full"></div>;
      default:
        return <div className="w-2 h-2 bg-orange-500 rounded-full"></div>;
    }
  };

  // Log debug information to diagnose loading issue
  console.log('Dashboard render state:', {
    hasUser: !!user,
    hasSheetConfig: !!sheetConfig,
    clientDataLength: clientData?.length,
    isLoading,
    hasError: !!error,
  });

  // Show loading state while checking authentication and loading data
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mb-4" />
            <span className="text-gray-600">Loading dashboard data...</span>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Loading too long? Click to refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Error Loading Data
            </h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <Users className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Please Log In
            </h2>
            <p className="text-gray-600 mb-6">
              You need to be logged in to access your dashboard and client data.
            </p>
            <button
              onClick={() => setCurrentPage('login')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show setup prompt if no sheet is configured
  if (!sheetConfig) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <FileSpreadsheet className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connect Your Google Sheet
            </h2>
            <p className="text-gray-600 mb-6">
              To see your client data and dashboard statistics, you need to
              connect a Google Sheet first.
            </p>
            <button
              onClick={() => setCurrentPage('sheets-setup')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              Setup Google Sheets
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no client data
  if (!clientData || clientData.length === 0) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <FileText className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No Client Data Found
            </h2>
            <p className="text-gray-600 mb-6">
              Your Google Sheet is connected, but we couldn't find any client
              data. Make sure your sheet contains client information and is
              properly formatted.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleRefresh}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Data
              </button>
              <button
                onClick={() => setCurrentPage('sheets-setup')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Check Sheet Setup
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate stats from client data
  const getTotalsByStatus = () => {
    const stats = {
      total: clientData.length,
      completed: 0,
      inProgress: 0,
      pending: 0,
      statusBreakdown: {},
    };

    clientData.forEach((client) => {
      const status = client.status || 'Unknown';

      // Add to status breakdown
      stats.statusBreakdown[status] = (stats.statusBreakdown[status] || 0) + 1;

      // Add to summary categories
      if (status.toLowerCase().includes('complete')) {
        stats.completed++;
      } else if (
        status.toLowerCase().includes('progress') ||
        status.toLowerCase().includes('review')
      ) {
        stats.inProgress++;
      } else {
        stats.pending++;
      }
    });

    return stats;
  };

  const stats = getTotalsByStatus();
  const recentActivity = getRecentActivity();

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header with refresh */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <div className="flex items-center space-x-4">
            {lastFetched && (
              <span className="text-sm text-gray-500 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Updated {formatLastUpdated(lastFetched)}
              </span>
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
              />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Total Clients
                </h3>
                <p className="text-3xl font-bold text-blue-600">
                  {stats.total}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Completed
                </h3>
                <p className="text-3xl font-bold text-green-600">
                  {stats.completed}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  In Progress
                </h3>
                <p className="text-3xl font-bold text-orange-600">
                  {stats.inProgress}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Pending</h3>
                <p className="text-3xl font-bold text-yellow-600">
                  {stats.pending}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Breakdown */}
        {stats.statusBreakdown &&
          Object.keys(stats.statusBreakdown).length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow border mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Status Breakdown
                </h2>

                <div className="flex items-center">
                  <Filter className="w-4 h-4 text-gray-400 mr-2" />
                  <select
                    value={filteredStatus}
                    onChange={(e) => setFilteredStatus(e.target.value)}
                    className="text-sm border-gray-300 rounded-md"
                  >
                    <option value="all">All Statuses</option>
                    {Object.keys(stats.statusBreakdown).map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(stats.statusBreakdown)
                  .filter(
                    ([status]) =>
                      filteredStatus === 'all' || status === filteredStatus
                  )
                  .map(([status, count]) => (
                    <div
                      key={status}
                      className="text-center p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="text-2xl font-bold text-gray-900">
                        {count}
                      </div>
                      <div className="text-sm text-gray-600">{status}</div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full"
                          style={{ width: `${(count / stats.total) * 100}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {Math.round((count / stats.total) * 100)}% of total
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

        {/* Quick Actions and Recent Activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Quick Actions
            </h2>
            <div className="space-y-3">
              <button
                onClick={() => setCurrentPage('portals')}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">
                  View All Clients
                </div>
                <div className="text-sm text-gray-500">
                  Manage {stats.total} client records and statuses
                </div>
              </button>
              <button
                onClick={() => setCurrentPage('analytics')}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">View Analytics</div>
                <div className="text-sm text-gray-500">
                  Check performance metrics and trends
                </div>
              </button>
              <button
                onClick={() => setCurrentPage('configurator')}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">
                  Configure Portal
                </div>
                <div className="text-sm text-gray-500">
                  Customize your client portal settings
                </div>
              </button>
              <button
                onClick={() => setCurrentPage('sheets-setup')}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">
                  Google Sheets Setup
                </div>
                <div className="text-sm text-gray-500">
                  Update data source or mapping
                </div>
              </button>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Recent Activity
            </h2>
            <div className="space-y-3">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    {getActivityIcon(activity.type)}
                    <div className="text-sm text-gray-600 flex-1">
                      {activity.message}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 italic">
                  No recent activity data available.
                </div>
              )}
            </div>

            {recentActivity.length > 0 && (
              <button
                onClick={() => setCurrentPage('portals')}
                className="mt-4 text-sm text-blue-600 hover:text-blue-800"
              >
                View all client activities →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
